import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator, CommandShortcut,
} from "@/components/ui/command";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  LayoutGrid, FileStack, ClipboardCheck, BookOpen, FileSearch, Undo2, Scale, Plug, Settings as SettingsIcon,
  Sparkles, CheckCircle2, XCircle, ArrowRight,
} from "lucide-react";
import { listArtifacts } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutGrid, keywords: "dashboard kpi home" },
  { to: "/app/artifacts", label: "Candidate Artifacts", icon: FileStack, keywords: "queue list table" },
  { to: "/app/reviews", label: "Reviews", icon: ClipboardCheck, keywords: "my queue assigned" },
  { to: "/app/published", label: "Published Knowledge", icon: BookOpen, keywords: "approved published registry" },
  { to: "/app/evidence", label: "Evidence Console", icon: FileSearch, keywords: "audit chain custody export" },
  { to: "/app/revocations", label: "Revocations", icon: Undo2, keywords: "revoke freeze rebuild" },
  { to: "/app/policies", label: "Policies", icon: Scale, keywords: "rules engine compliance" },
  { to: "/app/integrations", label: "Integrations", icon: Plug, keywords: "zendesk confluence okta connectors" },
  { to: "/app/settings", label: "Settings", icon: SettingsIcon, keywords: "workspace admin config" },
];

export default function CommandPalette({ open, onOpenChange, onAction }) {
  const navigate = useNavigate();
  const loc = useLocation();
  const [artifacts, setArtifacts] = useState([]);

  useEffect(() => {
    if (open) listArtifacts({}).then((r) => setArtifacts(r.items || [])).catch(() => {});
  }, [open]);

  const onReview = useMemo(() => /^\/app\/artifacts\/[A-Z0-9-]+$/.test(loc.pathname), [loc.pathname]);
  const currentArtifactId = onReview ? loc.pathname.split("/").pop() : null;

  const go = (to) => { onOpenChange(false); navigate(to); };
  const action = (kind) => { onOpenChange(false); onAction?.(kind); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <DialogTitle className="sr-only">Command palette</DialogTitle>
      <DialogDescription className="sr-only">
        Search artifacts, jump to a screen, or run a contextual action.
      </DialogDescription>
      <CommandInput placeholder="Search artifacts, jump to screen, or run an action…" data-testid="cmdk-input" />
      <CommandList>
        <CommandEmpty>Nothing matches.</CommandEmpty>

        {onReview && (
          <>
            <CommandGroup heading={`Actions · ${currentArtifactId}`}>
              <CommandItem onSelect={() => action("ai-refine")} data-testid="cmdk-action-ai-refine">
                <Sparkles /> <span>Refine with AI</span>
                <CommandShortcut>⌘ I</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => action("approve")} data-testid="cmdk-action-approve">
                <CheckCircle2 /> <span>Approve</span>
                <CommandShortcut>⌘ ⇧ A</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => action("reject")} data-testid="cmdk-action-reject">
                <XCircle /> <span>Reject</span>
                <CommandShortcut>⌘ ⇧ R</CommandShortcut>
              </CommandItem>
              <CommandItem onSelect={() => action("toggle-diff")} data-testid="cmdk-action-diff">
                <ArrowRight /> <span>Toggle existing vs proposed diff</span>
                <CommandShortcut>⌘ D</CommandShortcut>
              </CommandItem>
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        <CommandGroup heading="Navigate">
          {NAV.map((n) => (
            <CommandItem
              key={n.to}
              value={`${n.label} ${n.keywords}`}
              onSelect={() => go(n.to)}
              data-testid={`cmdk-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <n.icon /> <span>{n.label}</span>
            </CommandItem>
          ))}
        </CommandGroup>

        {artifacts.length > 0 && (
          <>
            <CommandSeparator />
            <CommandGroup heading="Jump to candidate artifact">
              {artifacts.slice(0, 12).map((a) => (
                <CommandItem
                  key={a.id}
                  value={`${a.id} ${a.title} ${a.domain}`}
                  onSelect={() => go(`/app/artifacts/${a.id}`)}
                  data-testid={`cmdk-artifact-${a.id}`}
                >
                  <FileStack />
                  <span className="font-mono text-xs text-muted-foreground mr-2">{a.id}</span>
                  <span className="truncate max-w-md">{a.title}</span>
                  <span className="ml-auto"><StatusBadge value={a.status} /></span>
                </CommandItem>
              ))}
            </CommandGroup>
          </>
        )}
      </CommandList>
    </CommandDialog>
  );
}

/** Hook: wire Cmd-K / Ctrl-K globally. */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [setOpen]);
  return { open, setOpen };
}
